<template>
  <header class="my-6 flex flex-row header-wrap font-alexandria w-full">
    <!-- Logo -->
    <!--div
      class="flex flex-row items-center hover:scale-110 transition-transform cursor-pointer"
      @click="router.push('landing')"
    >
      <img alt="Product logo" class="logo" src="@/assets/logo.svg" width="48" height="48" />
      <p class="text-2xl">{{ appName }}</p>
    </div-->
    <div
      class="flex flex-row grow mx-6 rounded-sm items-center justify-between px-2"
      :class="{
        'py-4 bg-white shadow-md text-black': currentRouteName !== 'landing',
        'text-white': currentRouteName === 'landing',
      }"
    >
      <div
        class="flex flex-row items-center hover:scale-110 transition-transform cursor-pointer"
        @click="router.push('landing')"
      >
        <p class="text-3xl ml-4">Scu4<span class="text-yellow">CHANGE</span></p>
      </div>

      <!-- Navigation -->
      <div class="flex flex-row items-end relative">
        <button
          :src="HamburgerIcon"
          class="w-8 h-8 lg:hidden text-black bg-yellow rounded-md p-1 mr-4 z-10 cursor-pointer"
          @pointerdown="showMenu()"
        >
          <svg
            class=""
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <nav
          v-if="menuVisible"
          class="flex flex-col rounded-b-2xl lg:flex-row lg:items-center text-sm absolute lg:relative right-0 top-0 max-lg:-right-2 py-2 lg:py-0"
          :class="{
            'bg-white lg:bg-transparent text-black max-lg:top-12': currentRouteName !== 'landing',
            'text-white bg-transparent max-lg:top-12': currentRouteName === 'landing',
          }"
        >
          <LocalisedLink
            v-for="link of pageLinks"
            :key="link.to"
            :to="link.to"
            class="mx-2 lg:mx-0 my-2 lg:my-1 uppercase"
          >
            <div class="cursor-pointer px-2">
              <p
                class="hover:underline underline-offset-4 decoration-yellow decoration-2 whitespace-nowrap"
                :class="{
                  underline: currentRouteName === link.to,
                }"
              >
                {{ t(link.label) }}
              </p>
            </div>
          </LocalisedLink>

          <!-- Language selector -->
          <div
            class="flex flex-row justify-start items-center relative text-yellow ml-4 max-lg:my-2 max-lg:mb-4 uppercase"
          >
            <div
              @pointerdown.stop="selectLanguage = !selectLanguage"
              class="hover:scale-110 transition-transform cursor-pointer flex flex-row items-center"
            >
              <p>{{ locale }}</p>
              <img
                class="w-3 mr-3 ml-0.5 h-2 cursor-pointer fill-yellow"
                @pointerdown.stop="selectLanguage = !selectLanguage"
                :src="DownArrow"
              />
            </div>
            <div
              class="absolute bg-gray-100 dark:bg-black justify-between z-20 p-1 top-8 -right-4 max-lg:left-0 rounded-lg"
              v-if="selectLanguage"
            >
              <div class="relative flex flex-col">
                <span
                  v-for="locale of locales"
                  :key="locale.code"
                  class="rounded bg-gray-100 dark:bg-black text-black dark:text-white p-1 mb-1 text-center cursor-pointer whitespace-nowrap"
                  @pointerdown="selectALocale(locale.code)"
                >
                  {{ locale.name }}
                </span>
              </div>
            </div>
          </div>

          <!-- Dynamic auth button: login or logout based on user state -->
          <button
            v-if="!appStore.getters.user.value"
            @click="emit('showLogin')"
            class="mx-4 my-2 px-2 py-1 lg:ml-4 lg:m-0 rounded-lg border border-yellow text-yellow hover:bg-yellow hover:text-black transition-colors uppercase text-sm"
          >
            {{ t('login') }}
          </button>
          <button
            v-else
            @click="appStore.actions.logout()"
            class="mx-4 my-2 px-2 py-1 lg:ml-4 lg:m-0 bg-yellow rounded-lg border border-yellow text-black hover:bg-black hover:text-yellow transition-colors uppercase text-sm"
          >
            {{ t('logout') }}
          </button>
        </nav>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useI18nStore, SupportedLocales } from '@/composition/i18nStore'
import LocalisedLink from '@/components/LocalisedLink.vue'
import DownArrow from '@/assets/down-arrow-yellow.svg'
import HamburgerIcon from '@/assets/hamburger.svg'
import useAppStore from '@/composition/appStore'

interface PageLink {
  to: string
  label: string
}

const emit = defineEmits<{
  showLogin: []
}>()

const router = useRouter()
const appStore = useAppStore()
const { actions: i18nActions } = useI18nStore()
const { locale } = useI18n({ useScope: 'global' })
const { t } = useI18n({})

const pageLinks: PageLink[] = [
  {
    to: 'landing',
    label: 'landingRouteLabel',
  },
  {
    to: 'favourites',
    label: 'favouritesRouteLabel',
  },
  {
    to: 'stories',
    label: 'storiesRouteLabel',
  },
  {
    to: 'about',
    label: 'ourTeamRouteLabel',
  },
  {
    to: 'build',
    label: 'buildRouteLabel',
  },
  {
    to: 'discover',
    label: 'discoverRouteLabel',
  },
]

const selectLanguage = ref(false)
const menuVisible = ref(true)
let timer: number

function showMenu() {
  menuVisible.value = !menuVisible.value
  if (!menuVisible.value) {
    selectLanguage.value = false
  }
  timer = setTimeout(() => (menuVisible.value = false), 10000)
}

window.onresize = () => {
  const width = window.innerWidth
  clearTimeout(timer)
  menuVisible.value = width >= 1024 // `lg` breakpoint in pixels
}

const locales = computed(() =>
  Object.keys(SupportedLocales).map((c) => {
    const code = c as keyof typeof SupportedLocales
    return { code, name: SupportedLocales[code].name }
  }),
)

const currentRouteName = computed(() => router.currentRoute.value.name)

async function selectALocale(l: keyof typeof SupportedLocales) {
  const newLocale = i18nActions.switchLanguage(l)
  selectLanguage.value = false
  router.push({ params: { locale: newLocale } })
}
</script>

<style scoped></style>
